import { apiClient } from "@/api/api-client";
import createBaseService from "@/api/base-service";
import type { BomSupplierInfo, CreateBomSupplierInfoData, UpdateBomSupplierInfoData } from "../../types/bom-supplier-info";

export type BomSupplierInfoOption = {
    id: string;
    supplier_name: string | null;
};

const BASE_URL = "/bom/supplier-infos";

const SKIP_FORM_DATA_KEYS = new Set(["created_at", "updated_at"]);

function appendValue(formData: FormData, key: string, value: unknown) {
    if (value === undefined) return;
    if (value === null) {
        formData.append(key, "");
        return;
    }
    if (value instanceof File) {
        formData.append(key, value);
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item, idx) => appendValue(formData, `${key}[${idx}]`, item));
        return;
    }
    if (typeof value === "object") {
        Object.entries(value as Record<string, unknown>).forEach(([k, v]) => appendValue(formData, `${key}[${k}]`, v));
        return;
    }
    if (typeof value === "boolean") {
        formData.append(key, value ? "1" : "0");
        return;
    }
    formData.append(key, String(value));
}

function toFormData(data: Record<string, unknown>, extra?: Record<string, string>): FormData {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
        if (SKIP_FORM_DATA_KEYS.has(key)) return;
        appendValue(formData, key, value);
    });
    if (extra) {
        Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
    }
    return formData;
}

const bomSupplierInfoService = {
    ...createBaseService<BomSupplierInfo, CreateBomSupplierInfoData, UpdateBomSupplierInfoData>(BASE_URL),
    create: async (data: CreateBomSupplierInfoData): Promise<BomSupplierInfo> => {
        const formData = toFormData(data as Record<string, unknown>);
        const response = await apiClient.post<{ data: BomSupplierInfo }>(BASE_URL, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data.data;
    },
    updateById: async (id: string, data: UpdateBomSupplierInfoData): Promise<BomSupplierInfo> => {
        const formData = toFormData(data as Record<string, unknown>, { _method: "PUT" });
        const response = await apiClient.post<{ data: BomSupplierInfo }>(`${BASE_URL}/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data.data;
    },
    options: async (): Promise<BomSupplierInfoOption[]> => {
        const response = await apiClient.get<BomSupplierInfoOption[]>(`${BASE_URL}/options`);
        return response.data;
    },
};

export default bomSupplierInfoService;
